import type { ActivityDefinition, PlanDefinition } from '@medplum/fhirtypes';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { SettingsPageFrame } from '@eluve/blocks';
import {
  Eyebrow,
  MarkdownProse,
  PlanShapeBadge,
  PlanShapeSection,
  fetchPlanActivityDefinitions,
  shapeAccent,
} from '@eluve/care-plan-render';
import {
  Badge,
  Button,
  Caption,
  Heading,
  Icon,
  LinkButton,
  Skeleton,
  Text,
} from '@eluve/components';
import { useMedplum } from '@eluve/feature-patients';
import { planShapeOf } from '@eluve/plan-shapes';

import { FhirJsonDialog } from './FhirJsonDialog';
import { planConditions, planDefinitionTitle } from './care-plans';

const CarePlanDetailContent: React.FC = () => {
  const { planId } = useParams<{ planId: string }>();
  const { client } = useMedplum();

  const [plan, setPlan] = useState<PlanDefinition | null>(null);
  const [activityDefinitions, setActivityDefinitions] = useState<
    ActivityDefinition[] | undefined
  >(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!client || !planId) {
      return;
    }
    let cancelled = false;
    // Clear the previous plan's state when planId changes, so navigating
    // between plans doesn't briefly render the new PlanDefinition enriched with
    // the prior plan's activities (or leave stale enrichment if the activity
    // fetch fails after setPlan).
    setPlan(null);
    setActivityDefinitions(undefined);
    setError(null);
    (async () => {
      try {
        const pd = await client.readResource('PlanDefinition', planId);
        if (cancelled) {
          return;
        }
        setPlan(pd);
        // Resolved ActivityDefinitions enrich activity entries with their
        // exercise-intrinsic fields (description, patient instructions).
        const ads = await fetchPlanActivityDefinitions(client, pd);
        if (!cancelled) {
          setActivityDefinitions(ads);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, planId]);

  if (error) {
    return (
      <Text className="text-text-negative-strong">
        Failed to load this care plan template: {error}
      </Text>
    );
  }

  if (!plan) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-2/3 rounded-lg" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl opacity-60" />
      </div>
    );
  }

  const shape = planShapeOf(plan);
  const conditions = planConditions(plan);

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Eyebrow className={shape ? shapeAccent(shape).text : undefined}>
            Template
          </Eyebrow>
          <Heading size="lg" weight="semibold" level={2} className="mt-1">
            {planDefinitionTitle(plan)}
          </Heading>
          <div className="mt-1 inline-flex items-center gap-2">
            {plan.version ? (
              <Text inline size="xs" color="secondary" className="font-mono">
                v{plan.version}
              </Text>
            ) : null}
            <PlanShapeBadge shape={shape} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <FhirJsonDialog
            resource={plan}
            title="FHIR JSON"
            description="The stored PlanDefinition resource."
            trigger={
              <Button variant="outline">
                <Icon name="Code2" size="auto" />
                View FHIR
              </Button>
            }
          />
          <LinkButton to=".." variant="outline">
            <Icon name="ArrowLeft" size="auto" />
            All care plans
          </LinkButton>
        </div>
      </div>

      <div className="space-y-4 py-6">
        {plan.description && (
          <MarkdownProse
            content={plan.description}
            className="max-w-3xl text-sm text-foreground"
          />
        )}
        {conditions.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <Caption inline color="secondary">
              Conditions
            </Caption>
            {conditions.map((c) => (
              <Badge key={c} variant="secondary">
                {c}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <PlanShapeSection plan={plan} activityDefinitions={activityDefinitions} />
    </div>
  );
};

export const CarePlanDetailPage: React.FC = () => (
  <SettingsPageFrame size="wide" parentTitle="Care Plan Templates" backTo="..">
    <CarePlanDetailContent />
  </SettingsPageFrame>
);
